import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';
import { Plus, Search, MapPin, ShieldCheck, Package } from 'lucide-react';

export default function AdminFarmers() {
  const [farmers, setFarmers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('published');

  useEffect(() => {
    api.get(`/admin/farmers?status=${tab}`).then(r => { setFarmers(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [tab]);

  const filtered = farmers.filter(f =>
    f.name.toLowerCase().includes(search.toLowerCase()) ||
    f.farm_name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div data-testid="admin-farmers-page" className="p-6 lg:p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-serif font-medium">Farmers</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage your marketplace farmers</p>
        </div>
        <Link to="/admin/farmers/add">
          <Button data-testid="add-farmer-button" className="rounded-full"><Plus className="h-4 w-4 mr-2" /> Add Farmer</Button>
        </Link>
      </div>

      <Tabs value={tab} onValueChange={(v) => { setTab(v); setLoading(true); }}>
        <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
          <TabsList className="rounded-full">
            <TabsTrigger data-testid="farmers-tab-published" value="published" className="rounded-full">Listed</TabsTrigger>
            <TabsTrigger data-testid="farmers-tab-draft" value="draft" className="rounded-full">Drafts</TabsTrigger>
          </TabsList>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input data-testid="farmers-search-input" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search farmers..." className="pl-9 w-64 rounded-full" />
          </div>
        </div>

        <TabsContent value={tab}>
          {loading ? (
            <div className="flex justify-center py-20"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20"><p className="text-muted-foreground">No {tab} farmers found</p></div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map((f) => (
                <Link key={f.farmer_id} to={`/admin/farmers/${f.farmer_id}`} data-testid={`admin-farmer-card-${f.farmer_id}`}
                  className="bg-card rounded-xl border border-border p-5 hover:border-primary/30 transition-colors">
                  <div className="flex items-start gap-4">
                    <img src={f.profile_image} alt="" className="w-14 h-14 rounded-xl object-cover shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-sm truncate">{f.name}</h3>
                        {f.verification_status === 'verified' && <ShieldCheck className="h-3.5 w-3.5 text-primary shrink-0" />}
                      </div>
                      <p className="text-xs text-muted-foreground truncate">{f.farm_name}</p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                        <MapPin className="h-3 w-3" /> {f.location}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 mt-4 pt-3 border-t border-border">
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Package className="h-3 w-3" /> {f.product_count || 0} products
                    </div>
                    <Badge variant={f.status === 'published' ? 'default' : 'secondary'} className="rounded-full text-xs ml-auto capitalize">{f.status}</Badge>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
